/**
 * The order an assignment (or any row carrying both spellings) belongs to.
 *
 * `workOrderId` is the current field; `manufacturingOrderId` is the
 * deprecated one older rows still carry. Every surface that asks "which
 * order is this on" reads it through here, so the fallback lives in one place.
 */
export interface OrderRef {
	workOrderId?: number | null
	manufacturingOrderId?: number | null
}

export const orderIdOf = (ref: OrderRef | null | undefined): number | undefined =>
	ref?.workOrderId ?? ref?.manufacturingOrderId ?? undefined;

/** A short code reads as a batch number as it is ("B-204", "24017"); a longer label gives the number it ends in. */
const batchIn = (text: string | null | undefined): string | null => {
	const value = (text ?? "").trim();
	if (!value) return null;
	if (value.length <= 12 && !/\s/.test(value) && /\d/.test(value)) return value;
	const match = /(\d+)(?:\s*\/\s*\d+)?\s*$/.exec(value);
	return match ? match[1] : null;
};

/**
 * The order's batch number: its own, the "Batch #" field the plant fills in,
 * or the number its label ends in ("Adderall XR -DL · 11/28" → "11").
 */
export const batchOf = (order: { batchNumber?: string | null; attributeValues?: string; sku?: string | null }): string | null => {
	let field: string | null = null;
	try {
		const attributes = order.attributeValues ? (JSON.parse(order.attributeValues) as Record<string, unknown>) : {};
		field = attributes.batchNo == null ? null : String(attributes.batchNo);
	} catch {
		field = null;
	}
	return batchIn(order.batchNumber) ?? batchIn(field) ?? batchIn(order.sku);
};
